<template>
    <main class="essay" dir="rtl" lang="ar">
        <nav class="essay-wrap essay-breadcrumbs" aria-label="مسار الصفحة">
            <router-link :to="{ name: 'welcome' }">الرئيسية</router-link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">حل أسئلة المقالي</span>
        </nav>

        <section class="essay-hero" aria-labelledby="essay-title">
            <div class="essay-wrap essay-hero__grid">
                <div class="essay-hero__content">
                    <p class="essay-eyebrow">عربي + Languages · Programming &amp; AI</p>
                    <h1 id="essay-title">مش عارف تحل أسئلة المقالي؟</h1>
                    <p class="essay-hero__lead">دي مش مشكلة في فهمك… غالبًا محتاج طريقة للحل.</p>
                    <p class="essay-text essay-hero__text">
                        اتعلم إزاي تقرأ السؤال، تحدد المطلوب، تفتكر الـKeywords، ترتب إجابتك وتكتبها بشكل واضح —
                        سواء أنت عربي أو Languages.
                    </p>
                    <div class="essay-actions">
                        <a
                            v-if="config.pdfUrl"
                            :href="config.pdfUrl"
                            class="essay-btn essay-btn--primary"
                            download
                            @click="onPdfClick('hero')"
                        >
                            <lucide-icon name="Download" :size="20" aria-hidden="true" />
                            حمّل دليل حل أسئلة المقالي مجانًا
                        </a>
                        <button
                            v-if="config.youtubeUrl"
                            type="button"
                            class="essay-btn essay-btn--outline"
                            @click="openVideo('hero')"
                        >
                            <lucide-icon name="PlayCircle" :size="20" aria-hidden="true" />
                            شاهد الفيديو
                        </button>
                        <router-link
                            v-if="!config.pdfUrl && !config.youtubeUrl"
                            :to="config.grade11CourseRoute"
                            class="essay-btn essay-btn--primary"
                            @click.native="onCourseCta('hero')"
                        >
                            <lucide-icon name="Rocket" :size="20" aria-hidden="true" />
                            ابدأ تتدرب مع Coding With Ghonaim
                        </router-link>
                    </div>
                    <div class="essay-hero__tags" aria-label="الفئات المستفيدة">
                        <span>للطلاب العربي واللغات</span>
                        <span class="ltr-inline">Grade 10 + Grade 11</span>
                    </div>
                </div>

                <div class="essay-hero__visual" aria-label="من الإجابة الفارغة إلى إجابة منظمة">
                    <div class="essay-answer-box essay-answer-box--empty">
                        <span>Answer Box</span>
                        <i></i><i></i><i></i>
                    </div>
                    <lucide-icon name="ArrowDown" :size="30" aria-hidden="true" />
                    <div class="essay-answer-box essay-answer-box--ready">
                        <strong>Keywords</strong>
                        <span>Reason → Key terms → Result</span>
                        <b>SHORT + CLEAR + CORRECT</b>
                    </div>
                </div>
            </div>
            <div class="essay-wrap essay-method-strip" aria-label="خطوات حل السؤال المقالي">
                <div v-for="step in methodSteps" :key="step.en" class="essay-method-strip__item">
                    <span>{{ step.ar }}</span>
                    <b class="ltr-inline">{{ step.en }}</b>
                </div>
            </div>
        </section>

        <section class="essay-section essay-section--answer" aria-labelledby="instant-answer-title">
            <div class="essay-wrap essay-reading">
                <p class="essay-kicker">إجابة سريعة قابلة للتطبيق</p>
                <h2 id="instant-answer-title">إزاي أحل السؤال المقالي؟</h2>
                <blockquote class="essay-direct-answer">
                    <strong>حل السؤال المقالي في 6 خطوات:</strong>
                    اقرأ السؤال كاملًا، حدد نوعه والمطلوب، افتكر الكلمات الأساسية، رتب عناصر الإجابة، اكتب إجابة
                    قصيرة وواضحة وصحيحة، ثم راجع أنك جاوبت كل المطلوب.
                </blockquote>
                <ol class="essay-six-grid">
                    <li v-for="step in methodSteps" :key="`quick-${step.en}`">
                        <span>{{ step.number }}</span>
                        <strong>{{ step.ar }}</strong>
                        <small class="ltr-inline">{{ step.en }}</small>
                    </li>
                </ol>
            </div>
        </section>

        <section class="essay-section" aria-labelledby="mcq-title">
            <div class="essay-wrap">
                <div class="essay-section-head essay-section-head--center">
                    <p class="essay-kicker">افهم المشكلة الأول</p>
                    <h2 id="mcq-title">ليه بحل MCQ ومبعرفش أحل مقالي؟</h2>
                    <p>أنت ممكن تكون فاهم المعلومة فعلًا. المشكلة إنك لسه محتاج تتدرب إزاي تطلعها من دماغك وتكتبها.</p>
                </div>
                <div class="essay-compare-grid">
                    <article>
                        <span class="essay-card-label">MCQ</span>
                        <h3 class="ltr-inline">Recognition</h3>
                        <p>السؤال → الاختيارات → تتعرف على الإجابة أو دليل عليها.</p>
                        <div class="essay-flow ltr-inline">Question → Options → Recognition</div>
                    </article>
                    <article class="essay-compare-grid__essay">
                        <span class="essay-card-label">ESSAY</span>
                        <h3 class="ltr-inline">Recall + Organization + Expression</h3>
                        <p>السؤال → تسترجع الفكرة → ترتبها → تكتبها.</p>
                        <div class="essay-flow ltr-inline">Question → Recall → Organize → Write</div>
                    </article>
                </div>
                <aside class="essay-myth">
                    <div class="essay-myth__bad"><b aria-hidden="true">✕</b><span>إجابة طويلة ومبهمة</span></div>
                    <div>
                        <h2>السؤال المقالي مش موضوع تعبير</h2>
                        <p>مش مطلوب تملا السطور. المطلوب تكتب عناصر الإجابة ومصطلحات المنهج.</p>
                    </div>
                    <div class="essay-myth__good"><b aria-hidden="true">✓</b><span>SHORT + CLEAR + CORRECT</span></div>
                </aside>
            </div>
        </section>

        <section id="six-step-system" class="essay-section essay-section--dark" aria-labelledby="system-title">
            <div class="essay-wrap">
                <div class="essay-section-head essay-section-head--center">
                    <p class="essay-kicker">The Six-Step System</p>
                    <h2 id="system-title">طريقة حل أسئلة المقالي خطوة بخطوة</h2>
                    <p>كل خطوة لها وظيفة. لما تمشي عليهم بالترتيب، الـAnswer Box مش هيبقى بداية مجهولة.</p>
                </div>
                <div class="essay-steps">
                    <article v-for="step in methodSteps" :key="`full-${step.en}`" class="essay-step">
                        <div class="essay-step__number">{{ step.number }}</div>
                        <div class="essay-step__icon"><lucide-icon :name="step.icon" :size="24" aria-hidden="true" /></div>
                        <p class="essay-step__en ltr-inline">{{ step.en }}</p>
                        <h3>{{ step.ar }}</h3>
                        <p>{{ step.text }}</p>
                        <div class="essay-step__example" :dir="step.number === 1 || step.number === 3 || step.number === 5 ? 'ltr' : 'rtl'">
                            {{ step.example }}
                        </div>
                        <strong class="essay-step__focus ltr-inline">{{ step.focus }}</strong>
                    </article>
                </div>
                <div class="essay-center-action">
                    <a v-if="config.pdfUrl" :href="config.pdfUrl" download class="essay-btn essay-btn--primary" @click="onPdfClick('six_steps')">
                        حمّل دليل الطريقة مجانًا
                    </a>
                </div>
            </div>
        </section>

        <section class="essay-section" aria-labelledby="commands-title">
            <div class="essay-wrap">
                <div class="essay-section-head">
                    <p class="essay-kicker">Command Words</p>
                    <h2 id="commands-title">أهم كلمات أسئلة المقالي ومعنى كل واحدة</h2>
                    <p>الـCommand Word هو أول مفتاح لشكل الإجابة. اقرأ الجدول من اليمين أو استخدم التمرير على الموبايل.</p>
                </div>
                <div class="essay-table-wrap" tabindex="0" aria-label="جدول كلمات أسئلة المقالي">
                    <table>
                        <thead><tr><th>Command Word</th><th>بالعربي</th><th>المطلوب</th><th>Answer Shape</th><th>Example</th></tr></thead>
                        <tbody>
                            <tr v-for="row in commandWords" :key="row[0]">
                                <td class="ltr-cell">{{ row[0] }}</td><td>{{ row[1] }}</td><td>{{ row[2] }}</td>
                                <td class="ltr-cell">{{ row[3] }}</td><td class="ltr-cell">{{ row[4] }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </section>

        <section class="essay-section essay-section--soft" aria-labelledby="language-title">
            <div class="essay-wrap">
                <div class="essay-language-hero">
                    <div>
                        <p class="essay-kicker">عربي + Languages</p>
                        <h2 id="language-title">الطريقة واحدة سواء أنت عربي أو Languages</h2>
                        <p>اللغة تتغير. <strong>طريقة التفكير لا تتغير.</strong></p>
                    </div>
                    <div class="essay-language-hero__map ltr-inline">READ → IDENTIFY → RECALL → ORGANIZE → WRITE → CHECK</div>
                </div>
                <div class="essay-language-grid">
                    <article>
                        <span>سؤال عربي</span>
                        <h3>لماذا تعد المصادقة متعددة العوامل أكثر أمانًا؟</h3>
                        <p>حدد: لماذا. افتكر: أكثر من عامل، كلمة المرور، عامل إضافي. ثم اكتب السبب مباشرة.</p>
                    </article>
                    <article dir="ltr">
                        <span>Languages Question</span>
                        <h3>Why is MFA more secure than using only a password?</h3>
                        <p>Identify: Why. Recall: different factors, password, second factor. Then write the reason.</p>
                    </article>
                </div>
                <div class="essay-writing-grid">
                    <article>
                        <h3>مش بعرف أكتب الإجابة بالإنجليزي — أعمل إيه؟</h3>
                        <p>ما تحاولش تكتب English معقد. استخدم كلمات المنهج وجمل قصيرة وقواعد بسيطة.</p>
                        <ul dir="ltr" class="essay-sentence-starters">
                            <li><b>Definition:</b> X is…</li><li><b>Purpose:</b> X is used to…</li>
                            <li><b>Reason:</b> Because…</li><li><b>Compare:</b> X uses…, while Y uses…</li>
                            <li><b>Result:</b> This causes…</li><li><b>Importance:</b> X is important because…</li>
                        </ul>
                        <strong class="essay-rule ltr-inline">Meaning before fancy English.</strong>
                    </article>
                    <article>
                        <h3>أكتب الإجابة العربي إزاي؟</h3>
                        <ul><li>استخدم المصطلح العلمي.</li><li>جاوب مباشرة.</li><li>تجنب المقدمات العامة.</li><li>ما تكررش السؤال.</li></ul>
                        <div class="essay-mini-transform"><del>المصادقة مهمة جدًا في الأمان…</del><ins>المصادقة هي التحقق من هوية المستخدم.</ins></div>
                    </article>
                </div>
            </div>
        </section>

        <section class="essay-section" aria-labelledby="techniques-title">
            <div class="essay-wrap">
                <div class="essay-techniques">
                    <article>
                        <p class="essay-kicker">Retrieval Technique</p>
                        <h2 id="techniques-title">مش فاكر الإجابة؟ حول السؤال لـMCQ في دماغك</h2>
                        <p class="ltr-block">What is the main purpose of encryption?</p>
                        <div class="essay-options"><span>Confidentiality</span><span>Authentication</span><span>Compression</span><span>Website Identity</span></div>
                        <p>اختَر <strong>Confidentiality</strong>، ثم حوّلها لجملة:</p>
                        <blockquote dir="ltr">Encryption protects the confidentiality of data.</blockquote>
                        <div class="essay-big-flow ltr-inline">MCQ IN YOUR HEAD → ESSAY ON PAPER</div>
                    </article>
                    <article>
                        <p class="essay-kicker">Scenario Questions</p>
                        <h2>السؤال طويل وScenario بيخوفني — أعمل إيه؟</h2>
                        <ol><li>اقرأ المطلوب.</li><li>علم على الـclues.</li><li>تجاهل التفاصيل غير المهمة.</li><li>حدد الـconcept.</li><li>حدد الـcommand word.</li><li>جاوب فقط المطلوب.</li></ol>
                        <div class="essay-big-flow ltr-inline">LONG STORY ↓ CLUES ↓ CONCEPT ↓ ANSWER</div>
                    </article>
                </div>
            </div>
        </section>

        <section class="essay-section essay-section--examples" aria-labelledby="examples-title">
            <div class="essay-wrap">
                <div class="essay-section-head essay-section-head--center">
                    <p class="essay-kicker">Worked Examples</p>
                    <h2 id="examples-title">أمثلة محلولة من Programming &amp; AI</h2>
                    <p>شوف طريقة التفكير قبل الإجابة النهائية، مش الإجابة وحدها.</p>
                </div>
                <div class="essay-example-list">
                    <article v-for="(example, index) in workedExamples" :key="example.question" class="essay-example">
                        <header><span>{{ example.type }}</span><b>{{ index + 1 }}</b></header>
                        <h3 :dir="example.type === 'عربي' ? 'rtl' : 'ltr'">{{ example.question }}</h3>
                        <dl>
                            <div><dt>IDENTIFY</dt><dd>{{ example.identify }}</dd></div>
                            <div><dt>KEYWORDS</dt><dd class="essay-keywords"><span v-for="word in example.keywords" :key="word">{{ word }}</span></dd></div>
                            <div><dt>FINAL ANSWER</dt><dd :dir="example.type === 'عربي' ? 'rtl' : 'ltr'">{{ example.answer }}</dd></div>
                            <div><dt>CHECK</dt><dd>{{ example.check }}</dd></div>
                        </dl>
                    </article>
                </div>
                <div class="essay-center-action">
                    <router-link :to="config.grade11CourseRoute" class="essay-btn essay-btn--primary" @click.native="onCourseCta('examples')">
                        تدرب مع Coding With Ghonaim
                    </router-link>
                </div>
            </div>
        </section>

        <section class="essay-section" aria-labelledby="answers-title">
            <div class="essay-wrap">
                <div class="essay-section-head">
                    <p class="essay-kicker">Answer Quality</p>
                    <h2 id="answers-title">إجابة شكلها صح… لكنها مش كفاية</h2>
                    <p>الإجابة العامة قد تبدو منطقية، لكنها لا تثبت إنك كتبت الفكرة العلمية المطلوبة.</p>
                </div>
                <div class="essay-transform-list">
                    <article v-for="item in transformations" :key="item.question">
                        <h3 :dir="/^[A-Za-z]/.test(item.question) ? 'ltr' : 'rtl'">{{ item.question }}</h3>
                        <div class="essay-transform__weak"><b>ضعيفة</b><span :dir="/^[A-Za-z]/.test(item.weak) ? 'ltr' : 'rtl'">{{ item.weak }}</span><small>{{ item.why }}</small></div>
                        <div class="essay-transform__strong"><b>أفضل</b><span :dir="/^[A-Za-z]/.test(item.strong) ? 'ltr' : 'rtl'">{{ item.strong }}</span></div>
                    </article>
                </div>
                <aside class="essay-ten-two">
                    <div><span>10</span><b>MCQ</b></div><lucide-icon name="ArrowLeft" :size="28" aria-hidden="true" />
                    <div><span>2</span><b>Essay</b></div>
                    <div class="essay-ten-two__copy"><h2>تدريب بسيط يخليك تتحسن في المقالي</h2><p>كل 10 MCQ → اختَر 2 → اخفِ الاختيارات → افتكر الـKeyword → اكتب → راجع.</p></div>
                </aside>
            </div>
        </section>

        <section v-if="config.youtubeUrl" id="essay-video" class="essay-section essay-section--dark" aria-labelledby="video-title">
            <div class="essay-wrap essay-reading">
                <div class="essay-section-head essay-section-head--center">
                    <p class="essay-kicker">Full Video Guide</p>
                    <h2 id="video-title">شاهد الشرح كامل: إزاي تتخلص من خوف أسئلة المقالي؟</h2>
                    <p>الفيديو يشرح ليه المقالي بيخوفك، الست خطوات، Command Words، والفرق بين إجابة ضعيفة وإجابة قصيرة وواضحة وصحيحة — لطلاب العربي واللغات.</p>
                </div>
                <button v-if="!videoPlaying" type="button" class="essay-video-facade" @click="playVideo">
                    <img :src="config.youtubeThumbnail" alt="شاهد فيديو إزاي تحل أسئلة المقالي — Coding With Ghonaim" width="1280" height="720" loading="lazy" decoding="async" />
                    <span><lucide-icon name="Play" :size="32" aria-hidden="true" /></span>
                </button>
                <iframe
                    v-else
                    :src="playingEmbedUrl"
                    title="إزاي تحل أسئلة المقالي؟ — Coding With Ghonaim"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowfullscreen
                ></iframe>
                <div class="essay-video-meta">
                    <h3>هتتعلم من الفيديو</h3>
                    <ul>
                        <li>ليه الـMCQ أسهل من المقالي</li>
                        <li>READ → IDENTIFY → RECALL → ORGANIZE → WRITE → CHECK</li>
                        <li>إزاي تتعامل مع Explain وCompare وWhy وScenario</li>
                        <li>كتابة إجابة قصيرة بالعربي والإنجليزي</li>
                    </ul>
                    <a :href="config.youtubeUrl" target="_blank" rel="noopener noreferrer" class="essay-btn essay-btn--outline" @click="onYoutubeOpen">
                        مشاهدة الفيديو على YouTube
                    </a>
                </div>
            </div>
        </section>

        <section v-if="config.pdfUrl" id="essay-guide" class="essay-section essay-section--download" aria-labelledby="pdf-title">
            <div class="essay-wrap essay-download">
                <div><p class="essay-kicker">Free A4 Guide</p><h2 id="pdf-title">حمّل دليل أسئلة المقالي مجانًا</h2>
                    <p>ملف A4 عربي + Languages يمشي معاك من قراءة السؤال لكتابة الإجابة.</p>
                    <ul><li>الست خطوات</li><li>Command Words</li><li>Answer Blueprints</li><li>English sentence starters</li><li>Scenario method</li><li>Practice checklist</li></ul>
                    <div class="essay-actions">
                        <a :href="config.pdfUrl" download class="essay-btn essay-btn--primary" @click="onPdfClick('pdf_section')">تحميل الـPDF مجانًا</a>
                        <button v-if="config.youtubeUrl" type="button" class="essay-btn essay-btn--outline" @click="openVideo('pdf_section')">شاهد الفيديو أولًا</button>
                    </div>
                </div>
            </div>
        </section>

        <section class="essay-section essay-section--mindmap" aria-labelledby="mindmap-title">
            <div class="essay-wrap">
                <div class="essay-section-head essay-section-head--center">
                    <p class="essay-kicker">Essay Mind Map</p>
                    <h2 id="mindmap-title">إزاي تحل السؤال المقالي؟ — How to Solve an Essay Question</h2>
                    <p>طريقة حل السؤال المقالي: اقرأ، حدّد، افتكر، رتّب، اكتب، راجع.</p>
                </div>
                <figure class="essay-mindmap">
                    <div class="essay-mindmap__canvas" role="img" :aria-label="config.mindmapAlt">
                        <div class="essay-mindmap__brand">
                            <img :src="config.logoUrl" alt="شعار Coding With Ghonaim" width="148" height="56" />
                            <span>Coding With Ghonaim</span>
                        </div>
                        <p class="essay-mindmap__title">إزاي تحل السؤال المقالي؟</p>
                        <p class="essay-mindmap__subtitle ltr-block">How to Solve an Essay Question</p>
                        <div class="essay-mindmap__hub">
                            <strong>السؤال المقالي</strong>
                            <span class="ltr-inline">SHORT + CLEAR + CORRECT</span>
                        </div>
                        <ol class="essay-mindmap__steps">
                            <li v-for="step in mindmapSteps" :key="step.en">
                                <i>{{ step.number }}</i>
                                <b class="ltr-inline">{{ step.en }}</b>
                                <strong>{{ step.ar }}</strong>
                                <small :class="{ 'ltr-inline': step.hintEn }">{{ step.hint }}</small>
                            </li>
                        </ol>
                    </div>
                    <figcaption>طريقة حل السؤال المقالي: اقرأ، حدّد، افتكر، رتّب، اكتب، راجع.</figcaption>
                </figure>
                <div class="essay-actions essay-actions--center">
                    <a :href="config.mindmapImage" download class="essay-btn essay-btn--outline" @click="onMindmapDownload">تحميل الخريطة</a>
                    <a :href="config.mindmapSource" target="_blank" rel="noopener noreferrer" class="essay-btn essay-btn--ghost" @click="onMindmapOpen">فتح النسخة الكبيرة</a>
                </div>
            </div>
        </section>

        <section class="essay-section essay-section--brand" aria-labelledby="practice-title">
            <div class="essay-wrap">
                <div class="essay-section-head essay-section-head--center">
                    <p class="essay-kicker">Coding With Ghonaim System</p>
                    <h2 id="practice-title">عرفت الطريقة… دلوقتي محتاج تتدرب عليها</h2>
                    <p>الإجابة المقالي Skill، والـSkill تتحسن عبر: <b class="ltr-inline">PRACTICE → MISTAKES → FEEDBACK → RETRY</b></p>
                </div>
                <div class="essay-platform-grid">
                    <article v-for="feature in platformFeatures" :key="feature.number">
                        <div class="essay-platform-card__number">{{ feature.number }}</div>
                        <lucide-icon :name="feature.icon" :size="28" aria-hidden="true" />
                        <h3>{{ feature.title }}</h3><p>{{ feature.text }}</p><strong v-if="feature.note">{{ feature.note }}</strong>
                        <a v-if="feature.urlKey && config[feature.urlKey]" :href="config[feature.urlKey]" target="_blank" rel="noopener noreferrer" @click="onExternalFeature(feature)">
                            اكتشف المزيد
                        </a>
                    </article>
                </div>
                <div class="essay-system-flow" aria-label="نظام تحسين مهارة المقالي">
                    <span v-for="(item, index) in systemFlow" :key="item">
                        <b class="ltr-inline">{{ item }}</b><i v-if="index < systemFlow.length - 1">↓</i>
                    </span>
                </div>
                <div class="essay-center-action">
                    <router-link :to="config.grade11CourseRoute" class="essay-btn essay-btn--primary" @click.native="onCourseCta('system')">ابدأ مع Coding With Ghonaim</router-link>
                </div>
            </div>
        </section>

        <section class="essay-section" aria-labelledby="stats-title">
            <div class="essay-wrap">
                <div class="essay-section-head essay-section-head--center">
                    <p class="essay-kicker">Programming By Numbers</p>
                    <h2 id="stats-title">شغلنا في البرمجة بالأرقام</h2>
                    <p>الأرقام هنا توضح حجم التدريب والدعم، مش وعدًا بنتيجة واحدة لكل طالب.</p>
                </div>
                <article class="essay-grade-card">
                    <header><h3>تانية ثانوي / <span class="ltr-inline">Grade 11</span></h3></header>
                    <div class="essay-proof">
                        <p class="essay-proof__lead">{{ grade11Proof.subtitle }}</p>
                        <div class="essay-proof__hero">
                            <strong class="ltr-inline">{{ grade11Proof.hero.value }}</strong>
                            <span>{{ grade11Proof.hero.label }}</span>
                            <small>{{ grade11Proof.hero.context }}</small>
                        </div>
                        <div class="essay-proof__grid">
                            <div v-for="stat in grade11Proof.stats" :key="stat.label">
                                <strong>{{ stat.value }}</strong>
                                <p>{{ stat.label }}</p>
                                <small>{{ stat.text }}</small>
                            </div>
                        </div>
                        <p class="essay-stats__note">{{ grade11Proof.note }}</p>
                        <div class="essay-center-action">
                            <router-link
                                :to="config.grade11CourseRoute"
                                class="essay-btn essay-btn--primary"
                                @click.native="onCourseCta('grade11_proof')"
                            >
                                ابدأ مع Coding With Ghonaim
                            </router-link>
                        </div>
                    </div>
                </article>
                <article class="essay-grade-card essay-grade-card--g10">
                    <header><h3>أولى ثانوي / <span class="ltr-inline">Grade 10</span></h3></header>
                    <div class="essay-system-tags"><span>محاضرات منظمة</span><span>عربي + Languages</span><span>Homework</span><span>Quizzes</span><span>أسئلة الكتاب</span><span>مراجعات</span></div>
                </article>
            </div>
        </section>

        <section class="essay-section essay-section--soft" aria-labelledby="difference-title">
            <div class="essay-wrap">
                <div class="essay-section-head essay-section-head--center"><h2 id="difference-title">ليه Coding With Ghonaim مختلف في Programming &amp; AI؟</h2></div>
                <div class="essay-power-grid">
                    <article v-for="power in coursePowers" :key="power[0]"><lucide-icon :name="power[2]" :size="25" aria-hidden="true" /><h3 class="ltr-inline">{{ power[0] }}</h3><p>{{ power[1] }}</p></article>
                </div>
                <h2 class="essay-subtitle-center">الطالب معانا بيعمل إيه كل أسبوع؟</h2>
                <ol class="essay-journey">
                    <li v-for="item in studentJourney" :key="item[0]"><b class="ltr-inline">{{ item[0] }}</b><span>{{ item[1] }}</span></li>
                </ol>
            </div>
        </section>

        <section class="essay-section" aria-labelledby="for-you-title">
            <div class="essay-wrap">
                <div class="essay-section-head essay-section-head--center"><h2 id="for-you-title">الصفحة دي ليك لو…</h2></div>
                <ul class="essay-for-you">
                    <li v-for="item in forYouItems" :key="item"><lucide-icon name="Check" :size="19" aria-hidden="true" />{{ item }}</li>
                </ul>
                <div class="essay-center-action"><a v-if="config.pdfUrl" :href="config.pdfUrl" download class="essay-btn essay-btn--primary" @click="onPdfClick('for_you')">ابدأ بالدليل المجاني</a></div>
            </div>
        </section>

        <section id="faq" class="essay-section essay-section--faq" aria-labelledby="faq-title">
            <div class="essay-wrap essay-reading">
                <div class="essay-section-head essay-section-head--center">
                    <p class="essay-kicker">Frequently Asked Questions</p>
                    <h2 id="faq-title">أسئلة شائعة عن حل المقالي وCoding With Ghonaim</h2>
                </div>
                <section v-for="(group, groupIndex) in faqGroups" :key="group.title" class="essay-faq-group">
                    <h3>{{ group.title }}</h3>
                    <div class="essay-faq">
                        <div v-for="(item, itemIndex) in group.items" :key="item[0]" class="essay-faq__item">
                            <h4>
                                <button
                                    type="button"
                                    :id="`faq-button-${groupIndex}-${itemIndex}`"
                                    :aria-expanded="isFaqOpen(groupIndex, itemIndex) ? 'true' : 'false'"
                                    :aria-controls="`faq-panel-${groupIndex}-${itemIndex}`"
                                    @click="toggleFaq(groupIndex, itemIndex, item[0])"
                                >
                                    <span>{{ item[0] }}</span><lucide-icon name="ChevronDown" :size="20" aria-hidden="true" />
                                </button>
                            </h4>
                            <div
                                v-show="isFaqOpen(groupIndex, itemIndex)"
                                :id="`faq-panel-${groupIndex}-${itemIndex}`"
                                class="essay-faq__answer"
                                role="region"
                                :aria-labelledby="`faq-button-${groupIndex}-${itemIndex}`"
                            ><p>{{ item[1] }}</p></div>
                        </div>
                    </div>
                </section>
            </div>
        </section>

        <section class="essay-section essay-section--author" aria-labelledby="author-title">
            <div class="essay-wrap essay-author">
                <img src="@/assets/welcome/ghonaim-portrait.webp" alt="م/ محمد غنيم — Coding With Ghonaim" width="220" height="220" loading="lazy" decoding="async" />
                <div><p class="essay-kicker">إعداد وشرح</p><h2 id="author-title">م/ محمد غنيم</h2><h3 class="ltr-inline">Coding With Ghonaim</h3>
                    <p>مؤسس Coding With Ghonaim، ويركز على تعليم Programming &amp; Artificial Intelligence لطلاب Grade 10 وGrade 11 من خلال الفهم والتطبيق والتدريب.</p>
                    <router-link to="/about" class="essay-text-link">اعرف أكتر عن م/ محمد غنيم</router-link>
                    <p class="essay-reviewed">آخر مراجعة: {{ formattedReviewDate }}</p>
                </div>
            </div>
        </section>

        <section class="essay-final" aria-labelledby="final-title">
            <div class="essay-wrap essay-reading">
                <p class="essay-kicker">الخطوة التالية</p>
                <h2 id="final-title">فهمت الطريقة؟ الخطوة اللي بعدها إنك تتدرب.</h2>
                <p>المقالي مش محتاج تحفظ إجابات أكتر. محتاج تفهم السؤال، تفتكر الفكرة، ترتبها، وتكتبها بوضوح.</p>
                <div class="essay-final__method"><span>اقرأ</span><span>حدّد</span><span>افتكر</span><span>رتّب</span><span>اكتب</span><span>راجع</span></div>
                <strong class="essay-final__line ltr-inline">READ → IDENTIFY → RECALL → ORGANIZE → WRITE → CHECK</strong>
                <h3>هات السؤال… أنت عارف تبدأ منين.</h3>
                <div class="essay-actions essay-actions--center">
                    <a v-if="config.pdfUrl" :href="config.pdfUrl" download class="essay-btn essay-btn--primary" @click="onPdfClick('final')">حمّل دليل المقالي مجانًا</a>
                    <button v-if="config.youtubeUrl" type="button" class="essay-btn essay-btn--outline" @click="openVideo('final')">شاهد الفيديو</button>
                    <router-link :to="config.grade11CourseRoute" class="essay-btn essay-btn--light" @click.native="onCourseCta('final')">ابدأ مع Coding With Ghonaim</router-link>
                </div>
                <small>عربي + Languages · Grade 10 + Grade 11 Programming &amp; AI</small>
            </div>
        </section>

        <section class="essay-section essay-section--related" aria-labelledby="related-title">
            <div class="essay-wrap">
                <h2 id="related-title">كمل مذاكرتك</h2>
                <div class="essay-related-grid">
                    <router-link v-for="resource in relatedResources" :key="resource.title" :to="resource.to" @click.native="onRelated(resource.title)">
                        <lucide-icon :name="resource.icon" :size="25" aria-hidden="true" /><strong>{{ resource.title }}</strong><span>{{ resource.text }}</span>
                    </router-link>
                </div>
                <div class="essay-official-links">
                    <h3>روابط Coding With Ghonaim</h3>
                    <div class="essay-official-links__grid">
                        <a
                            v-for="link in officialLinks"
                            :key="link.title"
                            :href="link.href"
                            target="_blank"
                            rel="noopener noreferrer"
                            @click="onRelated(link.title)"
                        >
                            <strong>{{ link.title }}</strong>
                            <span>{{ link.text }}</span>
                        </a>
                    </div>
                </div>
            </div>
        </section>

        <div v-if="config.pdfUrl" class="essay-mobile-sticky">
            <a :href="config.pdfUrl" download @click="onPdfClick('mobile_sticky')"><lucide-icon name="Download" :size="18" aria-hidden="true" />حمّل دليل المقالي</a>
        </div>
    </main>
</template>

<script>
import LucideIcon from '@/components/welcome/landing/LucideIcon.vue';
import { grade11Proof } from '@/components/welcome/landing/grade11/grade-11-data';
import { trackSeoCta, trackSeoEvent, trackSeoPageView } from '@/tracking/seo-events';

const data = require('./essay-page-data.cjs');

export default {
    name: 'EssayQuestionsLanding',
    components: { LucideIcon },
    data() {
        return {
            config: data.essayPageConfig,
            methodSteps: data.methodSteps,
            commandWords: data.commandWords,
            workedExamples: data.workedExamples,
            transformations: data.transformations,
            platformFeatures: data.platformFeatures,
            programmingStats: data.programmingStats,
            grade11Proof,
            coursePowers: data.coursePowers,
            studentJourney: data.studentJourney,
            faqGroups: data.faqGroups,
            openFaqs: { '0-0': true },
            videoPlaying: false,
            trackedScroll: {},
            systemFlow: ['UNDERSTAND', 'SOLVE', 'MAKE MISTAKES', 'LEARN WHY', 'CONNECT IDEAS', 'ASK WHEN STUCK', 'TRY AGAIN', 'IMPROVE'],
            forYouItems: [
                'بتحل MCQ ومبتعرفش مقالي.', 'بتفهم الشرح لكن بتقف قدام الـAnswer Box.',
                'بتنسى الـModel Answer.', 'بتخاف تكتب English.', 'بتكتب كتير ومبتاخدش الدرجة.',
                'بتتلخبط في Explain / Compare / Why.', 'الـScenario الطويل بيخوفك.', 'محتاج تدريب أكتر مش شرح بس.',
            ],
            mindmapSteps: [
                { number: 1, en: 'READ', ar: 'اقرأ', hint: 'الموضوع والمطلوب' },
                { number: 2, en: 'IDENTIFY', ar: 'حدّد', hint: 'Command Word', hintEn: true },
                { number: 3, en: 'RECALL', ar: 'افتكر', hint: 'الكلمات الأساسية' },
                { number: 4, en: 'ORGANIZE', ar: 'رتّب', hint: 'شكل الإجابة' },
                { number: 5, en: 'WRITE', ar: 'اكتب', hint: 'قصيرة وواضحة' },
                { number: 6, en: 'CHECK', ar: 'راجع', hint: 'كل المطلوب' },
            ],
            officialLinks: [
                { title: 'كل الروابط', text: 'All Links', href: data.essayPageConfig.allLinksUrl },
                { title: 'قناة واتساب', text: 'WhatsApp Channel', href: data.essayPageConfig.whatsappChannelUrl },
                { title: 'قناة تليجرام', text: 'Telegram', href: data.essayPageConfig.telegramUrl },
                { title: 'تطبيق iOS', text: 'App Store', href: data.essayPageConfig.iosAppUrl },
                { title: 'تطبيق Android', text: 'Google Play', href: data.essayPageConfig.androidAppUrl },
            ],
            relatedResources: [
                { title: 'Grade 11 Programming & AI', text: 'ابدأ منهج تانية ثانوي', to: '/grade-11', icon: 'GraduationCap' },
                { title: 'Grade 10 Programming & AI', text: 'ابدأ منهج أولى ثانوي', to: '/grade-10', icon: 'Code2' },
                { title: 'منهج تانية ثانوي', text: 'الكتاب والفصول والموارد', to: '/grade-11-programming-ai-curriculum', icon: 'BookOpen' },
                { title: 'منهج أولى ثانوي', text: 'الكتاب والوحدات والموارد', to: '/grade-10-programming-ai-curriculum', icon: 'Library' },
            ],
        };
    },
    computed: {
        playingEmbedUrl() {
            const url = this.config.youtubeEmbedUrl || '';
            if (!url) return '';
            return url.indexOf('?') === -1 ? `${url}?autoplay=1` : `${url}&autoplay=1`;
        },
        formattedReviewDate() {
            const date = new Date(`${this.config.lastReviewed}T00:00:00`);
            return new Intl.DateTimeFormat('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' }).format(date);
        },
    },
    mounted() {
        trackSeoPageView('essay_questions_page_view');
        window.addEventListener('scroll', this.onScroll, { passive: true });
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.onScroll);
    },
    methods: {
        onScroll() {
            const height = document.documentElement.scrollHeight - window.innerHeight;
            if (height <= 0) return;
            const percent = Math.round((window.scrollY / height) * 100);
            [50, 90].forEach((mark) => {
                if (percent >= mark && !this.trackedScroll[mark]) {
                    this.$set(this.trackedScroll, mark, true);
                    trackSeoEvent(`essay_scroll_${mark}`);
                }
            });
        },
        isFaqOpen(group, item) {
            return !!this.openFaqs[`${group}-${item}`];
        },
        toggleFaq(group, item, question) {
            const key = `${group}-${item}`;
            this.$set(this.openFaqs, key, !this.openFaqs[key]);
            if (this.openFaqs[key]) trackSeoEvent('faq_open', { question });
        },
        onPdfClick(placement) {
            trackSeoCta('essay_pdf_click', placement, this.config.pdfUrl);
            trackSeoEvent('essay_pdf_download', { placement });
        },
        openVideo(placement) {
            trackSeoCta('essay_video_play', placement, this.config.youtubeUrl);
            document.getElementById('essay-video')?.scrollIntoView({ behavior: 'smooth' });
        },
        playVideo() {
            this.videoPlaying = true;
            trackSeoEvent('essay_video_play', { placement: 'video_facade' });
        },
        onYoutubeOpen() {
            trackSeoCta('essay_video_play', 'youtube_link', this.config.youtubeUrl);
        },
        onCourseCta(placement) {
            trackSeoCta('essay_course_cta', placement, this.config.grade11CourseRoute);
        },
        onExternalFeature(feature) {
            trackSeoCta(feature.event, 'practice_system', this.config[feature.urlKey]);
        },
        onMindmapOpen() {
            trackSeoEvent('mindmap_open');
        },
        onMindmapDownload() {
            trackSeoEvent('mindmap_download');
        },
        onRelated(title) {
            trackSeoEvent('essay_related_resource', { title });
        },
    },
};
</script>
